import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Target, BarChart3, Handshake } from "lucide-react";

const pillars = [
  { icon: Target, label: "Deal sourcing", desc: "Structured pipeline across regulated verticals" },
  { icon: BarChart3, label: "Evaluation", desc: "Underwriting discipline and market analysis" },
  { icon: Handshake, label: "Alignment", desc: "Long-term capital with operational depth" },
];

export default function StrategyCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="strategy"
      data-testid="strategy-cta-section"
      className="py-[30px] md:py-[75px]"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-white border border-black/[0.04] rounded-[1.5rem] overflow-hidden"
          style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
        >
          {/* Subtle top accent line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/40 to-transparent" />

          {/* Geometric grid accent */}
          <div className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(59,130,246,1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(59,130,246,1) 1px, transparent 1px)
              `,
              backgroundSize: '48px 48px',
            }}
          />

          <div className="relative z-10 p-8 md:p-12">
            {/* Top row: heading + description + CTA */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <div className="flex-1">
                <h2
                  data-testid="strategy-cta-heading"
                  className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-[16px]"
                >
                  Investment strategy
                </h2>
                <p className="font-body text-base md:text-lg leading-relaxed text-[#475569] max-w-xl md:max-w-none">
                  How we deploy capital, evaluate opportunities, and structure long-term alignment.
                </p>
              </div>
              <a
                href="/investment-strategy"
                data-testid="strategy-cta-button"
                className="group inline-flex items-center gap-2 px-[18px] py-[10px] bg-[#0B1220] hover:bg-[#111827] text-white text-[14px] font-semibold rounded-full transition-all duration-300 shadow-lg shadow-[#0B1220]/10 flex-shrink-0 self-start md:self-center hover:-translate-y-0.5"
              >
                Learn more
                <ArrowUpRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200/60 to-slate-200 mb-8" />

            {/* Three pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0">
              {pillars.map((pillar, i) => (
                <motion.div
                  key={pillar.label}
                  data-testid={`strategy-pillar-${i + 1}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.55, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={`flex items-start gap-4 px-0 md:px-6 transition-all duration-300 hover:-translate-y-0.5 ${
                    i < pillars.length - 1 ? "md:border-r md:border-slate-100" : ""
                  } ${i === 0 ? "md:pl-0" : ""} ${i === pillars.length - 1 ? "md:pr-0" : ""}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100/60 flex items-center justify-center flex-shrink-0">
                    <pillar.icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-body text-[15px] font-semibold text-[#08122a] mb-1">
                      {pillar.label}
                    </h3>
                    <p className="font-body text-sm text-[#475569] leading-relaxed md:whitespace-nowrap">
                      {pillar.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
